import {
    AuthContext,
    ClaimIdBadge,
    DataTable,
    DataTablePresenter,
    GuarantorIdBadge,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderCollectionPaymentsRequest,
    ProviderCollectionPayment,
    ProviderCollectionPaymentSortType,
    renderAmount,
    renderDateTime
} from '@enter/core';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseSortBy } from 'stores';
import * as S from './payments.styles';

interface IPaymentsProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IPaymentsState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const columns: ITableColumn<ProviderCollectionPayment>[] = [
    {
        id: 'CollectedAmount',
        Header: 'Collected Amount',
        accessor: 'collectedAmount',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderAmount(cell.value)}</S.Cell>,
    },
    {
        id: 'Source',
        Header: 'Source',
        accessor: 'source',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
        maxWidth: 100,
    },
    {
        id: 'ClaimID',
        Header: 'Claim',
        accessor: 'claimId',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{<ClaimIdBadge claimId={cell.value} />}</S.Cell>,
    },
    {
        id: 'GuarantorID',
        Header: 'Guarantor',
        accessor: 'guarantorId',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{<GuarantorIdBadge guarantorId={cell.value} />}</S.Cell>,
        minWidth: 150,
    },
    {
        id: 'PaymentDate',
        Header: 'Payment Date',
        accessor: 'paymentDate',
        sortType: ProviderCollectionPaymentSortType.PAYMENTDATE,
        defaultSort: { id: 'PaymentDate', desc: true },
        Cell: ({ cell }) => <S.Cell>{renderDateTime(cell.value)}</S.Cell>,
    },
];

const filterSchemas: IFilterSchema<keyof ListProviderCollectionPaymentsRequest>[] = [
    {
        label: 'Guarantor ID',
        filterId: 'filterGuarantorId',
        columnId: 'guarantorId',
        filterType: 'String',
    },
    {
        label: 'Claim ID',
        filterId: 'filterClaimId',
        columnId: 'claimId',
        filterType: 'String',
    },
    {
        label: 'Created',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
];

class PaymentsPage extends React.Component<IPaymentsProps, IPaymentsState> {
    private presenter: DataTablePresenter<ProviderCollectionPayment>;

    constructor(props: IPaymentsProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderCollectionPayment>({
            name: 'payments',
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Payments';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('payments');
    }

    public render() {
        return (
            <PageLayout>
                <PageHeader title="Collection Payments" hideDateRange={true} />
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable presenter={this.presenter} isLoading={this.state.loading} onFetch={this.onFetch} />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        try {
            this.setState({
                options: fetchOptions,
                loading: true,
            });
            this.authContext.cancelGroup('payments');
            this.authContext.setGroup('payments');

            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const sortAndOrder = parseSortBy(sortBy, columns);
            const listing = await this.props.rootStore.paymentStore.loadPaymentListing({
                page: pageIndex,
                pageSize,
                ...sortAndOrder,
                filters: validFilters,
            });
            this.presenter.onDataUpdate(listing.items || []);
            this.presenter.onDataTotalChange(listing.total || 0);
            this.setState({
                loading: false,
            });
        } catch (err) {
            this.dialogContext.showError(err);
        }
    };
}

export const Payments: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        return <PaymentsPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
    }),
);
